import { useState, useRef } from 'react'
import { Plus, Trash2, Copy, Check, RefreshCw, ExternalLink, X, Pencil } from 'lucide-react'
import { useCustomers, useCreateCustomer, useRenameCustomer, useDeleteCustomer, useRegenerateKey } from '../hooks/useCustomers'
import CustomerForm from './CustomerForm'

export default function CustomerList() {
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [copiedKey, setCopiedKey] = useState(null)
  const [newKeyInfo, setNewKeyInfo] = useState(null)
  const [renamingId, setRenamingId] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef(null)

  const { data: customers = [], isLoading } = useCustomers()
  const createCustomer = useCreateCustomer()
  const renameCustomer = useRenameCustomer()
  const deleteCustomer = useDeleteCustomer()
  const regenerateKey = useRegenerateKey()

  const handleCreateCustomer = async (customerData) => {
    try {
      await createCustomer.mutateAsync(customerData)
      setShowForm(false)
    } catch {
      // error toast shown by hook
    }
  }

  const handleDeleteCustomer = (customerId, customerName) => {
    if (!confirm(`Delete "${customerName}"? This will also remove their subscription.`)) return
    deleteCustomer.mutate(customerId)
  }

  const handleRegenerateKey = async (customerId, customerName) => {
    if (!confirm(`Regenerate API key for "${customerName}"? The old key will stop working immediately.`)) return
    try {
      const result = await regenerateKey.mutateAsync(customerId)
      const key = typeof result === 'string' ? result : result?.key || JSON.stringify(result)
      setNewKeyInfo({ customerId, key })
    } catch {
      // error toast shown by hook
    }
  }

  const startRename = (customer) => {
    setRenamingId(customer.tenant_id)
    setRenameValue(customer.customer_name)
    setTimeout(() => renameInputRef.current?.select(), 0)
  }

  const cancelRename = () => {
    setRenamingId(null)
    setRenameValue('')
  }

  const handleRename = (customerId) => {
    const trimmed = renameValue.trim()
    if (!trimmed) return
    renameCustomer.mutate(
      { customerId, newName: trimmed },
      { onSuccess: () => setRenamingId(null) },
    )
  }

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text)
    setCopiedKey(id)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const maskApiKey = (key) => {
    if (!key) return 'No key'
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`
  }

  if (isLoading) {
    return <div className="loading">Loading customers...</div>
  }

  return (
    <div className="customer-list">
      <div className="page-header">
        <h1>Customers</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          <Plus size={18} />
          Add Customer
        </button>
      </div>

      {/* New API key inline reveal */}
      {newKeyInfo && (
        <div className="new-key-card">
          <div className="new-key-header">
            <strong>New API Key generated</strong>
            <span>Copy it now — it won't be shown again after you dismiss this</span>
          </div>
          <div className="new-key-body">
            <code className="new-key-value">{newKeyInfo.key}</code>
            <button
              className="btn-secondary"
              onClick={() => copyToClipboard(newKeyInfo.key, 'new')}
            >
              {copiedKey === 'new' ? <Check size={14} /> : <Copy size={14} />}
              {copiedKey === 'new' ? 'Copied!' : 'Copy key'}
            </button>
          </div>
          <button className="new-key-dismiss" onClick={() => setNewKeyInfo(null)}>
            <X size={14} /> Dismiss
          </button>
        </div>
      )}

      {showForm && (
        <CustomerForm
          onSubmit={handleCreateCustomer}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div style={{ margin: '1rem 0' }}>
        <input
          type="text"
          placeholder="Search by name or tenant ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '320px', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '14px', background: 'var(--bg-page)', color: 'var(--text-primary)' }}
        />
      </div>

      <div className="customers-table">
        <table>
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Tenant ID</th>
              <th>API Key</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Events (30d)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  No customers yet. Click "Add Customer" to get started.
                </td>
              </tr>
            ) : (
              customers.filter(c => {
                if (!search) return true
                const q = search.toLowerCase()
                return c.customer_name?.toLowerCase().includes(q) || c.tenant_id?.toLowerCase().includes(q)
              }).map((customer) => (
                <tr key={customer.tenant_id}>
                  <td>
                    {renamingId === customer.tenant_id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input
                          ref={renameInputRef}
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRename(customer.tenant_id)
                            if (e.key === 'Escape') cancelRename()
                          }}
                          style={{ padding: '0.2rem 0.4rem', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid var(--border)', width: '180px' }}
                          disabled={renameCustomer.isPending}
                          autoFocus
                        />
                        <button className="icon-button" title="Save" onClick={() => handleRename(customer.tenant_id)} disabled={renameCustomer.isPending}>
                          <Check size={15} />
                        </button>
                        <button className="icon-button" title="Cancel" onClick={cancelRename} disabled={renameCustomer.isPending}>
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <strong>{customer.customer_name}</strong>
                        <button className="icon-button" title="Rename customer" onClick={() => startRename(customer)} style={{ opacity: 0.4 }}>
                          <Pencil size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td><code className="tenant-id">{customer.tenant_id}</code></td>
                  <td>
                    <div className="api-key-cell">
                      <code>{maskApiKey(customer.subscription_key)}</code>
                      {customer.subscription_key && (
                        <button
                          onClick={() => copyToClipboard(customer.subscription_key, customer.tenant_id)}
                          className="icon-button"
                          title="Copy full API key"
                        >
                          {copiedKey === customer.tenant_id ? <Check size={16} className="success" /> : <Copy size={16} />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${customer.tier || 'standard'}`}>{customer.tier || 'standard'}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${customer.is_active ? 'active' : 'inactive'}`}>
                      {customer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{customer.usage_30d ? customer.usage_30d.toLocaleString() : '0'} events</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleRegenerateKey(customer.tenant_id, customer.customer_name)}
                        className="icon-button"
                        title="Regenerate API key"
                        disabled={regenerateKey.isPending}
                      >
                        <RefreshCw size={16} />
                      </button>
                      <a
                        href="https://numberskills.azure-api.net/monitoring/v2/health"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="icon-button"
                        title="Test API"
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button
                        onClick={() => handleDeleteCustomer(customer.tenant_id, customer.customer_name)}
                        className="icon-button danger"
                        title="Delete customer"
                        disabled={deleteCustomer.isPending}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
